import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import SnackbarUtil from './SnackbarUtil';

/**
 * Get the authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * Remove authentication data from localStorage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userType');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

/**
 * Handle logout and redirect to login page, preserving the current URL as redirect param
 */
export const handleAuthFailure = (): void => {
  clearAuthData();
  SnackbarUtil.error('Session expired. Please log in again.');
  const redirect = encodeURIComponent(window.location.pathname);
  window.location.href = `/login?redirect=${redirect}`;
};

/**
 * Create configured axios instance with JWT interceptors
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create();

  // Request interceptor to add JWT token
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAuthToken();

      if (token) {
        // Add Authorization header if token exists
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor to handle 401 errors (session expired/not authenticated)
  // 403 errors are handled at the component level (user is authenticated but lacks access)
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        handleAuthFailure();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Export configured axios instance
export const axiosInstance = createAxiosInstance();
